import { format } from 'date-fns'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import Spinner from '../../components/spinner/Spinner'
import { MiniHeader } from '../../components/typography/miniHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { ORDERSTATUS } from '../../constants'
import Layout from '../../layout'
import { useGetSingleCustomersQuery } from '../../redux-toolkits/user/user.slice'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
import OrderTable from '../orders/OrderTable'

export const SingleCustomerPage: React.FC = () => {
  const { id } = useParams()

  const [query, setQuery] = useState<QueryProps & { status: string }>({
    skip: 0,
    limit: 20,
    search: undefined,
    status: 'all',
  })

  const {
    data: customer,
    isFetching,
    isLoading,
  } = useGetSingleCustomersQuery({
    id: id ?? '',
    query,
  })

  const filteredOrders = customer?.data?.orders?.filter((order) =>
    order.harmonizedReference
      .toLowerCase()
      .includes(query.search?.toLowerCase() ?? ''),
  )

  return (
    <Layout>
      {isLoading ? (
        <Spinner fullScreen={false} />
      ) : (
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-2 text-sec-black">
            <div
              className="
            w-[80px] h-[80px]
            rounded-full bg-[#4F122A]
            flex items-center justify-center
            text-lg text-white font-medium
            "
            >
              {customer?.data.firstname.charAt(0)}
            </div>
            <p className="text-[40px] text-black font-recoleta font-semibold capitalize">
              {customer?.data.firstname} {customer?.data.lastname}
            </p>
            <p>
              Joined{' '}
              {customer?.data?.createdAt
                ? format(customer?.data.createdAt, 'MMM dd, yyyy')
                : ''}
            </p>
            <div className="flex items-center gap-2">
              <Icon id="envelope" width={24} height={24} />
              <p>{customer?.data.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Icon id="phone-icon" width={24} height={24} />
              <p>+{customer?.data.phoneNumber}</p>
            </div>
            <div className="flex items-center gap-2">
              <p>Customer&apos;s account status:</p>
              <Status
                text={customer?.data?.deleted ? 'Inactive' : 'Active'}
                type={customer?.data?.deleted ? 'fail' : 'success'}
              />
            </div>
          </div>

          <CountCardContainer className="!my-0">
            <CountCard
              count={customer?.data.orderCount ?? 0}
              text="Total orders"
              isCurrency={false}
            />
            <CountCard
              count={customer?.data.totalSpend ?? 0}
              text="Total spend"
              isCurrency={true}
            />
          </CountCardContainer>

          <div className="flex flex-col gap-6">
            <MiniHeader name="Order History" />

            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="!w-full md:!max-w-[400px]">
                <SearchInput
                  searchFunction={(search) => {
                    setQuery({
                      ...query,
                      search: search === '' ? undefined : search,
                      skip: 0,
                    })
                  }}
                  placeholder="Search"
                />
              </div>

              <Dropdown
                menuClassName=""
                menuButton={
                  <Button
                    label={`Filter: ${query.status
                      .split('_')
                      .join(' ')
                      .charAt(0)}${query.status
                      .split('_')
                      .join(' ')
                      .slice(1)
                      .toLowerCase()}`}
                    primary={false}
                    icon="filter"
                    className="!py-3 !px-4 !text-base !font-normal !w-full md:max-w-fit"
                  />
                }
                menuItems={[
                  {
                    name: 'All',
                    value: 'all',
                    onClick: () => {
                      setQuery({
                        ...query,
                        page: 1,
                        skip: 0,
                        status: 'all',
                      })
                    },
                  },
                  ...ORDERSTATUS.map((status) => {
                    const name = status.name.split('_').join(' ')
                    return {
                      name: `${name.charAt(0)}${name.slice(1).toLowerCase()}`,
                      value: status.name,
                      onClick: () => {
                        setQuery({
                          ...query,
                          page: 1,
                          skip: 0,
                          status: status.name,
                        })
                      },
                    }
                  }),
                ]}
              />
            </div>

            <OrderTable
              orders={
                filteredOrders?.map((order) => {
                  return {
                    order: order,
                    orderItems: [],
                  }
                }) ?? []
              }
              currentPage={1}
              totalDataCount={filteredOrders?.length ?? 0}
              pageLimit={Number(query.limit)}
              onPageChange={() => {}}
              loading={isFetching}
              showCustomerName={false}
              name="Order History"
              showName={false}
            />
          </div>
        </div>
      )}
    </Layout>
  )
}
